import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Settings, 
  Clock, 
  Shield, 
  Bell, 
  Save, 
  Info,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { useAccessControl } from '../hooks/useAccessControl';
import DurationSelector from './DurationSelector';

interface PatientAccessSettingsProps {
  className?: string;
}

const PatientAccessSettings: React.FC<PatientAccessSettingsProps> = ({ className }) => {
  const { settings, updatePatientSettings, loading } = useAccessControl();
  const [localSettings, setLocalSettings] = useState({
    auto_approve_doctors: false,
    auto_approve_duration: '30 days',
    require_justification: true,
    allow_emergency_access: true,
    notification_preferences: {
      email_notifications: true,
      sms_notifications: false,
      push_notifications: true,
      emergency_notifications: true
    }
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Sincronizar com configurações carregadas
  useEffect(() => {
    if (settings) {
      setLocalSettings({
        auto_approve_doctors: settings.auto_approve_doctors || false,
        auto_approve_duration: settings.auto_approve_duration || '30 days',
        require_justification: settings.require_justification !== false,
        allow_emergency_access: settings.allow_emergency_access !== false,
        notification_preferences: {
          email_notifications: true,
          sms_notifications: false,
          push_notifications: true,
          emergency_notifications: true,
          ...settings.notification_preferences
        }
      });
      setHasChanges(false);
    }
  }, [settings]);

  const handleSettingChange = (key: string, value: any) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleNotificationChange = (key: string, value: boolean) => {
    setLocalSettings(prev => ({
      ...prev,
      notification_preferences: {
        ...prev.notification_preferences,
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await updatePatientSettings({
        auto_approve_doctors: localSettings.auto_approve_doctors,
        auto_approve_duration: localSettings.auto_approve_duration,
        require_justification: localSettings.require_justification,
        allow_emergency_access: localSettings.allow_emergency_access,
        notification_preferences: localSettings.notification_preferences
      });

      if (result.success) {
        toast.success('Configurações salvas com sucesso!');
        setHasChanges(false);
      } else {
        toast.error(result.error || 'Erro ao salvar configurações');
      }
    } catch (error) {
      toast.error('Erro interno do servidor');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setLocalSettings({
        auto_approve_doctors: settings.auto_approve_doctors || false,
        auto_approve_duration: settings.auto_approve_duration || '30 days',
        require_justification: settings.require_justification !== false,
        allow_emergency_access: settings.allow_emergency_access !== false,
        notification_preferences: {
          email_notifications: true,
          sms_notifications: false,
          push_notifications: true,
          emergency_notifications: true,
          ...settings.notification_preferences
        }
      });
      setHasChanges(false);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Configurações de Acesso</h3>
          <p className="text-gray-600">Defina suas preferências para solicitações de acesso aos seus dados</p>
        </div>
        {hasChanges && (
          <Badge className="bg-blue-100 text-blue-800">
            Alterações não salvas
          </Badge>
        )}
      </div>

      {/* Configurações de Duração Padrão */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            <CardTitle>Duração Padrão de Acesso</CardTitle>
          </div>
          <CardDescription>
            Define por quanto tempo os médicos terão acesso aos seus dados por padrão
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <DurationSelector
            selectedDuration={localSettings.auto_approve_duration}
            onDurationChange={(duration) => handleSettingChange('auto_approve_duration', duration)}
            defaultDuration="30 days"
          />
          
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Esta duração será sugerida automaticamente quando você aprovar solicitações de acesso. 
              Você ainda poderá alterar a duração para cada solicitação individual.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Configurações de Segurança */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-600" />
            <CardTitle>Configurações de Segurança</CardTitle>
          </div>
          <CardDescription>
            Controle como as solicitações de acesso são processadas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Aprovação Automática */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Aprovação Automática</h4>
              <p className="text-sm text-gray-500 mt-1">
                Aprovar automaticamente solicitações de médicos conhecidos
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.auto_approve_doctors}
                onChange={(e) => handleSettingChange('auto_approve_doctors', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {localSettings.auto_approve_doctors && (
            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-700">
                <strong>Atenção:</strong> Com a aprovação automática ativada, médicos que já tiveram acesso 
                aprovado anteriormente receberão acesso automaticamente para futuras solicitações.
              </AlertDescription>
            </Alert>
          )}

          {/* Exigir Justificativa */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Exigir Justificativa</h4>
              <p className="text-sm text-gray-500 mt-1">
                Médicos devem fornecer uma justificativa detalhada para acessar seus dados
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.require_justification}
                onChange={(e) => handleSettingChange('require_justification', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Permitir Acesso de Emergência */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Permitir Acesso de Emergência</h4>
              <p className="text-sm text-gray-500 mt-1">
                Permitir que médicos solicitem acesso de emergência com durações mais curtas
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.allow_emergency_access}
                onChange={(e) => handleSettingChange('allow_emergency_access', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Configurações de Notificação */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-purple-600" />
            <CardTitle>Preferências de Notificação</CardTitle>
          </div>
          <CardDescription>
            Escolha como você quer ser notificado sobre atividades de acesso
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Email */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Notificações por Email</h4>
              <p className="text-sm text-gray-500 mt-1">
                Receber emails sobre solicitações e alterações de acesso
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.notification_preferences.email_notifications}
                onChange={(e) => handleNotificationChange('email_notifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Push */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Notificações Push</h4>
              <p className="text-sm text-gray-500 mt-1">
                Receber notificações instantâneas no navegador
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.notification_preferences.push_notifications}
                onChange={(e) => handleNotificationChange('push_notifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Emergência */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-red-50">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Notificações de Emergência</h4>
              <p className="text-sm text-gray-500 mt-1">
                Sempre receber notificações para solicitações de emergência
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.notification_preferences.emergency_notifications}
                onChange={(e) => handleNotificationChange('emergency_notifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Resumo das Configurações */}
      {hasChanges && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Info className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-medium text-blue-900">Resumo das Alterações</h4>
            </div>
            <div className="space-y-2 text-sm text-blue-800">
              <div>• Duração padrão: <strong>{localSettings.auto_approve_duration}</strong></div>
              <div>• Aprovação automática: <strong>{localSettings.auto_approve_doctors ? 'Ativada' : 'Desativada'}</strong></div>
              <div>• Exigir justificativa: <strong>{localSettings.require_justification ? 'Sim' : 'Não'}</strong></div>
              <div>• Acesso de emergência: <strong>{localSettings.allow_emergency_access ? 'Permitido' : 'Bloqueado'}</strong></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Botões de Ação */}
      <div className="flex gap-3 pt-4">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || loading || isSaving}
          className="flex-1"
          size="lg"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Salvando...' : 'Salvar Configurações'}
        </Button>
        
        {hasChanges && (
          <Button
            onClick={handleReset}
            disabled={loading || isSaving}
            variant="outline"
            size="lg"
          >
            Cancelar
          </Button>
        )}
      </div>

      <Alert>
        <CheckCircle className="h-4 w-4" />
        <AlertDescription>
          Suas configurações são aplicadas automaticamente a todas as futuras solicitações de acesso. 
          Você sempre pode alterar essas configurações ou modificar durações específicas durante a aprovação.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default PatientAccessSettings;